/**
 * ContributorsDirectory — right-side drawer that lists every contributor
 * to the current project as a card. Opened by clicking the "Contributor"
 * text label in the FilterBar.
 *
 * Per the 2026-05-13 design review (page 6 wireframes, decision T7) the
 * label-vs-pill split matters:
 *   - clicking the *pill* opens the multi-select dropdown (existing
 *     ContributorPicker behavior — quick "filter the map by these N
 *     people" interaction).
 *   - clicking the *label* opens this directory — a discovery surface
 *     where you can browse every contributor, read their card, and
 *     pivot the map filter to one person via "View on map".
 *
 * Scope discipline (PR #47): the card here is intentionally minimal —
 * initials avatar, display name, contribution count, and a "View on
 * map" action that sets selectedContributorIds=[id] and closes the
 * drawer. Per-contributor avatars, individual/organization badges,
 * mini theme chips, and the full-screen profile view are deferred to
 * PR #48 so this PR stays reviewable. The wireframe surface is the
 * same drawer; only the card density grows in PR #48.
 *
 * Data: GET listProjectContributors.php?projectsid=N&all=1 (PR #47),
 * which returns { all: [{id,name,count},...], total }. We fetch on
 * first open and cache for the lifetime of the panel; project switches
 * close the panel as a side effect of FilterContext clearing
 * selection state.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "./config";
import { useProject } from "./ProjectContext";
import { useFilter } from "./FilterContext";
import { ORG_TYPE_OPTIONS } from "./utils/constants";
import RightDrawer from "./RightDrawer";
import "./ContributorsDirectory.css";

// Wireframe I6 (PR #46): render a 1-word badge per card so a viewer can
// tell an individual contributor from an institutional one at a glance.
// We collapse the 5-option org-type taxonomy down to two badges:
//   individual / null  → "Individual"
//   anything else      → "Organization"
// The full-fidelity sub-type ("museum", "non-profit"…) still appears on
// the profile page itself; the directory card just needs the bucket.
function badgeForOrgType(orgType) {
  if (!orgType || orgType === "individual") return "Individual";
  const labeled = ORG_TYPE_OPTIONS.find((o) => o.value === orgType);
  if (labeled?.value === "individual") return "Individual";
  return "Organization";
}

function computeInitials(name) {
  if (!name || typeof name !== "string") return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  // Strip an email-looking trailing "@domain" so an email-only
  // contributor still gets meaningful initials from the local part.
  const cleaned = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
  const parts = cleaned.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ContributorsDirectory({ open, onClose }) {
  const navigate = useNavigate();
  const { project } = useProject();
  const projectId = project?.projectsid;
  const { setSelectedContributorIds } = useFilter();

  const [list, setList] = useState([]); // [{id,name,count}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  // Reset transient UI state whenever the drawer is dismissed so the
  // next open starts clean (no stale search term, no scroll position
  // baked into the card list).
  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  // Fetch the full contributor list when the drawer opens for a given
  // project. We deliberately re-fetch on every open rather than cache
  // across closes: contributor activity is dynamic (new places can be
  // submitted by anyone in another tab) and the payload is small.
  useEffect(() => {
    if (!open || !projectId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    axios
      .get(config.getApiUrl("listProjectContributors.php"), {
        params: { projectsid: projectId, all: 1 },
        signal: controller.signal,
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        setList(Array.isArray(res.data?.all) ? res.data.all : []);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setError("Could not load contributors.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, projectId]);

  // Client-side filter. The whole list is in memory (small N) so we
  // skip the server `?search=` round-trip and just substring-match on
  // the display name.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [list, query]);

  const handleViewOnMap = useCallback(
    (id) => {
      setSelectedContributorIds([id]);
      onClose?.();
    },
    [setSelectedContributorIds, onClose],
  );

  // PR #48: clicking the body of the card (the name/avatar area)
  // opens the full-screen public profile at /u/:userId. The "View on
  // map" button keeps its own behavior so users get two distinct
  // pivots — the directory is both a discovery surface AND a fast
  // filter shortcut.
  const handleOpenProfile = useCallback(
    (id) => {
      onClose?.();
      navigate(`/u/${id}`);
    },
    [navigate, onClose],
  );

  const subHeader = (
    <>
      <input
        type="search"
        className="contributors-directory__search"
        placeholder="Search contributors…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search contributors"
      />
      {!loading && !error ? (
        <div className="contributors-directory__count">
          {list.length === 0
            ? "No contributors yet"
            : filtered.length === list.length
              ? `${list.length} contributor${list.length === 1 ? "" : "s"}`
              : `${filtered.length} of ${list.length}`}
        </div>
      ) : null}
    </>
  );

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title="Contributors"
      ariaLabel="Contributors directory"
      closeLabel="Close contributors directory"
      className="right-drawer--contributors"
      zIndex={2000}
      subHeader={subHeader}
    >
      {loading ? (
        <div className="contributors-directory__status">Loading…</div>
      ) : error ? (
        <div className="contributors-directory__status contributors-directory__status--error">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="contributors-directory__status">
          {query ? "No matches." : "No contributors yet."}
        </div>
      ) : (
        <ul className="contributors-directory__list">
          {filtered.map((c) => {
                const initials = computeInitials(c.name);
                const badge = badgeForOrgType(c.org_type);
                const badgeModifier =
                  badge === "Organization"
                    ? "contributors-directory__badge--org"
                    : "contributors-directory__badge--individual";
                return (
                  <li key={c.id} className="contributors-directory__card">
                    {/* PR #48: name + avatar area becomes the profile-open
                        affordance. We use a real <button> for keyboard
                        accessibility — the whole row is clickable but
                        only this region triggers the navigation, so the
                        "View on map" pill stays a distinct target. */}
                    <button
                      type="button"
                      className="contributors-directory__profile-link"
                      onClick={() => handleOpenProfile(c.id)}
                      aria-label={`Open ${c.name || "this contributor"}'s profile`}
                    >
                      <span
                        className="contributors-directory__avatar"
                        aria-hidden="true"
                      >
                        {initials}
                      </span>
                      <span className="contributors-directory__meta">
                        <span
                          className="contributors-directory__name"
                          title={c.name}
                        >
                          {c.name || "Unknown"}
                        </span>
                        <span className="contributors-directory__sub-row">
                          {/* PR #46 (wireframe I6): Individual /
                              Organization pill, picked from
                              users.org_type. Visual weight stays low
                              so it doesn't compete with the name. */}
                          <span
                            className={`contributors-directory__badge ${badgeModifier}`}
                          >
                            {badge}
                          </span>
                          <span className="contributors-directory__count-line">
                            {c.count} contribution{c.count === 1 ? "" : "s"}
                          </span>
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="contributors-directory__action"
                      onClick={() => handleViewOnMap(c.id)}
                    >
                      View on map
                    </button>
                  </li>
                );
              })}
        </ul>
      )}
    </RightDrawer>
  );
}
